import {
  Button,
  Callout,
  Card,
  CodeTabs,
  Input,
  Label,
  MonoChip,
  StepCard,
  Terminal,
} from '@mailysend/ui'
import { createFileRoute } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { OWNERSHIP_BADGE } from '~/components/marketing/claims.ts'
import {
  DEPLOY_DURATION_LONG,
  DeployButton,
  DeployGuideLink,
} from '~/components/marketing/deploy.tsx'
import { PageShell } from '~/components/marketing/page-shell.tsx'
import {
  AnchorSection,
  Code,
  Com,
  Endpoint,
  Key,
  Lede,
  Mono,
  Str,
} from '~/components/marketing/prose.tsx'
import { DOC_GROUP_ORDER, DOC_SECTIONS } from '~/content/doc-sections.ts'
import { FRAMEWORK_GROUPS } from '~/content/frameworks.ts'
import { breadcrumbSchema, DEPLOY_URL, pageHead, techArticleSchemas } from '~/seo'

export const Route = createFileRoute('/docs')({
  /**
   * `?q=` is the target of the site-wide `SearchAction` in the root JSON-LD.
   * Declaring a search that goes nowhere is worse than declaring none, so the
   * sidebar filter below actually consumes it.
   */
  validateSearch: (search: Record<string, unknown>): { q?: string } => {
    const q = typeof search.q === 'string' ? search.q.trim() : ''
    return q ? { q } : {}
  },
  head: () =>
    pageHead({
      title: 'Docs',
      description:
        'MailySend API documentation: quickstart, domains and DNS, the emails API, recipes for Next.js, Rails, Django and every other stack, batch and scheduling, templates, audiences, broadcasts, automations, inbound mail, webhooks, SDKs and error codes.',
      path: '/docs',
      image: '/og/docs.png',
      jsonLd: [
        breadcrumbSchema([{ name: 'Docs', path: '/docs' }]),
        ...techArticleSchemas('/docs', DOC_SECTIONS),
      ],
    }),
  component: DocsPage,
})

const DNS_RECORDS = [
  // The include names the transport that actually sends, not a hosted
  // MailySend — there isn't one. This is the Cloudflare Email Service value
  // that `packages/providers/src/adapters/cloudflare.ts` issues by default;
  // SES and Resend domains get `include:amazonses.com` instead.
  ['TXT', 'send.yourdomain.com', 'v=spf1 include:_spf.mx.cloudflare.net ~all'],
  ['TXT', 'ms1._domainkey', 'p=MIGfMA0GCSq… (2048-bit DKIM)'],
  ['TXT', '_dmarc', 'v=DMARC1; p=none; rua=mailto:dmarc@…'],
]

const PROVIDER_ROWS = [
  ['Cloudflare', '$0.35 / 1k', 'Default · lowest latency', 'Zero'],
  ['Amazon SES', '$0.10 / 1k', 'Millions/month, cost-first', 'IAM key'],
  ['Resend', 'your plan', 'Zero-risk migration window', 're_ key'],
]

const ERROR_ROWS: [string, string, ReactNode][] = [
  ['401', 'invalid_api_key', 'Check the key’s environment'],
  ['403', 'domain_not_verified', 'Publish the DKIM record, then verify'],
  ['422', 'suppressed_recipient', 'Address hard-bounced before — see logs'],
  [
    '429',
    'rate_limited',
    <>
      Honour <Mono>Retry-After</Mono>; SDKs do it for you
    </>,
  ],
]

const WEBHOOK_EVENTS = [
  'email.sent',
  'email.delivered',
  'email.delivery_delayed',
  'email.opened',
  'email.clicked',
  'email.bounced',
  'email.complained',
  'email.received',
  'contact.unsubscribed',
  'broadcast.finished',
]

/**
 * Third column is honest labelling: one of these is a published package, the
 * rest are a generator run against the OpenAPI document this deployment serves.
 * Listing all nine as if they shipped would be the same kind of stale claim the
 * comparison pages exist to avoid.
 */
const SDKS = [
  ['Node.js / TypeScript', 'npm i mailysend', 'published'],
  ['Python', 'openapi-generator-cli · python', 'generate'],
  ['Go', 'openapi-generator-cli · go', 'generate'],
  ['Ruby', 'openapi-generator-cli · ruby', 'generate'],
  ['PHP (+ Laravel)', 'openapi-generator-cli · php', 'generate'],
  ['Java / Kotlin', 'openapi-generator-cli · java', 'generate'],
  ['.NET / C#', 'openapi-generator-cli · csharp', 'generate'],
  ['Rust', 'openapi-generator-cli · rust', 'generate'],
  ['Elixir', 'openapi-generator-cli · elixir', 'generate'],
]

function DocsSidebar() {
  const { q } = Route.useSearch()
  const [query, setQuery] = useState(q ?? '')

  const needle = query.trim().toLowerCase()
  const matches = needle
    ? DOC_SECTIONS.filter(
        (section) =>
          section.navLabel.toLowerCase().includes(needle) ||
          section.headline.toLowerCase().includes(needle) ||
          section.description.toLowerCase().includes(needle),
      )
    : DOC_SECTIONS

  return (
    <aside className="min-w-0 self-start lg:sticky lg:top-[92px] lg:max-w-[280px]">
      <nav
        aria-label="Documentation"
        className="flex max-h-[calc(100vh-130px)] flex-col gap-0.5 overflow-y-auto rounded-card border border-line bg-card px-3.5 py-4"
      >
        <div className="mb-2 px-2">
          <Label htmlFor="docs-filter" className="sr-only">
            Filter documentation
          </Label>
          <Input
            id="docs-filter"
            type="search"
            value={query}
            placeholder="Filter sections"
            onChange={(event) => setQuery(event.target.value)}
            className="h-9 text-[13.5px]"
          />
          {needle ? (
            <div className="mt-2 flex items-center justify-between gap-2 text-[12px] text-muted-2">
              {/* An empty filter that silently hides the whole nav is a dead
                  end, so the count is stated and the way back is one click. */}
              <span aria-live="polite">
                {matches.length} of {DOC_SECTIONS.length} sections
              </span>
              <button
                type="button"
                onClick={() => setQuery('')}
                className="rounded-chip px-1.5 py-0.5 text-accent hover:bg-tint"
              >
                Clear
              </button>
            </div>
          ) : null}
        </div>

        {DOC_GROUP_ORDER.map((group) => {
          const items = matches.filter((section) => section.group === group)
          if (items.length === 0) return null
          return (
            <div key={group} className="contents">
              <div className="ms-eyebrow px-2 pt-3 pb-1.5 text-[10.5px]">{group}</div>
              {items.map((section) => (
                <a
                  key={section.anchor}
                  href={`#${section.anchor}`}
                  className="rounded-sm px-2 py-1.5 text-[14px] text-muted no-underline hover:bg-tint hover:text-ink"
                >
                  {section.navLabel}
                </a>
              ))}
            </div>
          )
        })}

        <a
          href="/guides/deploy-to-cloudflare"
          className="mt-3 rounded-sm px-2 py-1.5 text-[14px] font-semibold text-accent no-underline hover:bg-tint"
        >
          Deploy guide →
        </a>
      </nav>
    </aside>
  )
}

function DocsPage() {
  return (
    <PageShell>
      <div className="ms-container pt-11">
        <p className="ms-eyebrow m-0 text-[11.5px]">DOCUMENTATION · API v1 · MIT LICENSED</p>
        <h1 className="ms-display-1 mt-3.5 mb-3">Docs</h1>
        <p className="m-0 max-w-[66ch] text-[17.5px] leading-[1.6] text-muted">
          Fifteen minutes end to end, or jump straight to your endpoint. One base URL for everything
          — your own deployment, plus <MonoChip size="md">/v1</MonoChip>. The samples below write it
          as <MonoChip size="md">https://your-deployment/v1</MonoChip>; there is no hosted MailySend
          API to point at instead.
        </p>
      </div>

      <div className="ms-container grid items-start gap-10 pt-8 pb-20 lg:grid-cols-[minmax(240px,280px)_minmax(0,1fr)]">
        <DocsSidebar />

        <article className="flex min-w-0 flex-col gap-14">
          <AnchorSection anchor="intro" heading="Introduction">
            <Lede>
              MailySend is a Resend-compatible email platform that runs on Cloudflare Workers. One
              REST API covers transactional sends, marketing broadcasts, automations, inbound mail
              and analytics — and if you already call Resend, the request bodies, status values and
              webhook names here are the ones you know. Everything is MIT licensed, so you can read
              the source, fork it, or deploy the platform into your own Cloudflare account.
            </Lede>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[
                ['BASE URL', 'your-deployment/v1'],
                ['AUTH', 'Bearer ms_live_…'],
                ['FORMAT', 'JSON · UTF-8'],
              ].map(([label, value]) => (
                <Card key={label} className="p-4">
                  <div className="ms-eyebrow mb-1.5 tracking-normal">{label}</div>
                  <div className="font-mono text-[13px]">{value}</div>
                </Card>
              ))}
            </div>
          </AnchorSection>

          {/*
            Deploy is step one, and it has to be, because every other step in
            this list addresses a deployment that does not exist yet.

            The order used to be install-SDK, send, tail, which reads as though
            there were a hosted MailySend to hold an API key for. There is not:
            the API a reader is about to call is the Worker in their own
            Cloudflare account, so until that Worker exists there is no base
            URL, no domain and no key. Opening on `npm install` asked people to
            configure a client against nothing.

            No step here is a command, and that is now a statement about the
            product rather than a style choice. `build:cf` ends by running
            scripts/ensure-resources.mjs inside Workers Builds, which creates
            the queues, the bucket, the database and the namespaces and writes
            their ids into the config wrangler deploys — so the terminal that
            used to sit under step one was describing a version of this that no
            longer exists. Step four is the dashboard for the same reason:
            asking someone to install a CLI and run a device-code login to look
            at mail they can already see is a tax, not a quickstart.

            Claiming is step two because it is the only step with a deadline.
            /setup is open to whoever reaches it first (server/api/setup.ts),
            so a deployment left unclaimed is a deployment anyone can take.
          */}
          <AnchorSection anchor="quickstart" heading="Quickstart">
            <Lede>
              Five steps from an empty Cloudflare account to a delivered email, and not one of them
              needs a terminal. The first is a button.
            </Lede>
            <div className="flex flex-col gap-3.5">
              <StepCard
                step={1}
                title="Deploy it to your Cloudflare account"
                variant="rule"
                description={`One click takes ${DEPLOY_DURATION_LONG}. Cloudflare forks the repo and the build creates everything the Worker binds to — the twelve queues, the D1 database, the R2 bucket and the KV namespaces — then hands you back a URL. That URL is your API base URL for every step below.`}
              >
                <div className="mt-2.5 flex flex-wrap items-center gap-2.5">
                  <DeployButton label="Deploy to Cloudflare" chip="1-CLICK" size="md" />
                  <DeployGuideLink label="Or deploy from your terminal" />
                </div>
              </StepCard>
              <StepCard
                step={2}
                title="Claim it before anyone else does"
                variant="rule"
                description="Open the URL Cloudflare gave you. A fresh deployment lands on /setup, where you register a passkey and save ten recovery codes — that is the whole sign-up, and there is no password to leak."
              >
                <Callout variant="warn" title="The claim window is open until you close it">
                  <p className="m-0">
                    Whoever reaches <Mono>/setup</Mono> first owns the instance, so claim it now
                    rather than after lunch. If the URL will be public before you get to it, set{' '}
                    <Mono>MS_REQUIRE_CLAIM_CODE=1</Mono> and the claim code is printed only to your
                    Worker log. <Mono>MS_OWNER_EMAIL</Mono> is a different thing — a restriction on{' '}
                    <em>which</em> address may claim, not a nomination of who owns it, and setting
                    both means the claim code wins. Full detail in{' '}
                    <a href="/guides/claim-your-instance">Claim your instance</a>.
                  </p>
                </Callout>
              </StepCard>
              <StepCard
                step={3}
                title="Add your sending domain"
                variant="rule"
                description="Domains → Add. On Cloudflare DNS the records are published and verified in seconds; anywhere else, copy the three below into your provider and hit verify. Until a domain verifies, nothing can leave."
              >
                <p className="mt-1.5 mb-0 text-[14.5px] leading-relaxed text-muted">
                  The exact records are in <a href="#domains">Domains &amp; DNS</a>.
                </p>
              </StepCard>
              <StepCard
                step={4}
                title="Send"
                variant="rule"
                description="Create a key under Settings → API keys, then call your own deployment. No SDK required — this is the whole API."
              >
                <Code className="mt-1.5">
                  {'curl https://your-deployment/v1/emails \\\n  -H '}
                  <Str>"Authorization: Bearer $MAILYSEND_API_KEY"</Str>
                  {' \\\n  -H '}
                  <Str>"Content-Type: application/json"</Str>
                  {" \\\n  -d '{\n    "}
                  <Str>"from"</Str>
                  {': '}
                  <Str>"MailySend &lt;hello@yourdomain.com&gt;"</Str>
                  {',\n    '}
                  <Str>"to"</Str>
                  {': ['}
                  <Str>"user@example.com"</Str>
                  {'],\n    '}
                  <Str>"subject"</Str>
                  {': '}
                  <Str>"Hello from the edge"</Str>
                  {',\n    '}
                  <Str>"html"</Str>
                  {': '}
                  <Str>"&lt;p&gt;It works.&lt;/p&gt;"</Str>
                  {"\n  }'"}
                </Code>
                <p className="mt-3 mb-0 text-[14.5px] leading-relaxed text-muted">
                  Already calling Resend? Point <Mono>RESEND_BASE_URL</Mono> at{' '}
                  <Mono>https://your-deployment/v1</Mono> and the official <Mono>resend</Mono>{' '}
                  client sends here instead — see <a href="/guides/migrate-from-resend">Migrate</a>.
                  For Next.js, Rails, Django, Python, Go and the rest, the recipe for your stack is
                  in <a href="#frameworks">Sending from your framework</a>.
                </p>
              </StepCard>
              <StepCard
                step={5}
                title="Watch it land"
                variant="rule"
                description="Open Logs in the dashboard you just deployed. Every message is there with its status — accepted, delivered, bounced — and the detail view carries the provider's own response, so a rejection tells you what the receiving server actually said."
              >
                <p className="mt-1.5 mb-0 text-[14.5px] leading-relaxed text-muted">
                  Prefer to watch from a shell? <Mono>npx mailysend tail</Mono> streams the same
                  events — see <a href="#cli">the CLI</a>.
                </p>
              </StepCard>
            </div>
            {/*
              The one honest caveat on the button path: the resources are
              created by the build, so a build command Cloudflare guessed wrong
              is a deploy that fails on a missing binding rather than a deploy
              that quietly skips a step. These are the two values from the
              README, which is where they are kept correct.
            */}
            <Callout variant="info" title="If the build command is ever wrong">
              <p className="m-0">
                The queues, database, bucket and namespaces are created by{' '}
                <Mono>pnpm run build:cf</Mono>, so if Cloudflare infers a different build command
                the deploy fails on a binding it cannot resolve. Under{' '}
                <strong>Workers → your Worker → Settings → Builds</strong>, the two values are{' '}
                <Mono>pnpm run build:cf</Mono> and{' '}
                <Mono>npx wrangler deploy -c apps/app/.output-cf/server/wrangler.json</Mono>. The{' '}
                <Mono>-c</Mono> matters — the config wrangler deploys from is the one Vite generates
                beside the bundle.{' '}
                <a href="/guides/deploy-to-cloudflare#troubleshoot">If the deploy fails →</a>
              </p>
            </Callout>
          </AnchorSection>

          <AnchorSection anchor="domains" heading="Domains & DNS">
            <Lede>
              Add a domain, then publish three records. If the domain is on Cloudflare DNS we write
              them for you and verify in seconds; anywhere else, copy them into your provider and
              hit verify.
            </Lede>
            <div className="overflow-x-auto rounded-tile border border-line bg-card">
              <table className="w-full min-w-[520px] border-collapse font-mono text-[12.5px]">
                <caption className="sr-only">DNS records required for a sending domain</caption>
                <thead>
                  <tr className="border-b border-line text-left text-muted-2">
                    <th className="w-20 px-3.5 py-3 font-normal">TYPE</th>
                    <th className="w-[200px] px-3.5 py-3 font-normal">NAME</th>
                    <th className="px-3.5 py-3 font-normal">VALUE</th>
                  </tr>
                </thead>
                <tbody>
                  {DNS_RECORDS.map(([type, name, value]) => (
                    <tr key={name} className="border-b border-line-soft last:border-0">
                      <td className="px-3.5 py-3">{type}</td>
                      <td className="px-3.5 py-3">{name}</td>
                      <td className="px-3.5 py-3 text-muted">{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 mb-0 text-[15px] leading-[1.7] text-muted">
              Every domain gets its own DKIM key pair, a click-tracking subdomain you control, and
              an optional custom return-path. DMARC reports are parsed for you — see{' '}
              <a href="/analytics#dmarc">DMARC analytics</a>.
            </p>
          </AnchorSection>

          <AnchorSection anchor="auth" heading="Authentication">
            <Lede>
              Keys are scoped by permission, domain and environment. A leaked <Mono>send-only</Mono>{' '}
              key can’t read your logs or export contacts.
            </Lede>
            <Code>
              {'curl https://your-deployment/v1/api-keys \\\n  -H '}
              <Str>"Authorization: Bearer $MAILYSEND_API_KEY"</Str>
              {' \\\n  -d \'{ "name": '}
              <Str>"prod worker"</Str>
              {', "permission": '}
              <Str>"sending_access"</Str>
              {',\n        "domain_id": '}
              <Str>"dom_9f2"</Str>
              {" }'"}
            </Code>
          </AnchorSection>

          <AnchorSection anchor="api" heading="Emails API">
            <div className="mb-4 flex flex-col gap-2">
              <Endpoint method="POST" path="/v1/emails" note="Send one email" />
              <Endpoint method="POST" path="/v1/emails/batch" note="Up to 100 per call" />
              <Endpoint method="GET" path="/v1/emails/:id" note="Status, events, full payload" />
              <Endpoint method="PATCH" path="/v1/emails/:id" note="Reschedule a queued email" />
              <Endpoint
                method="POST"
                tone="DEL"
                path="/v1/emails/:id/cancel"
                note="Cancel before send"
              />
            </div>
            <Card className="p-[18px]">
              <div className="ms-eyebrow mb-3 tracking-[0.1em]">REQUEST BODY</div>
              <div className="flex flex-col gap-2.5 text-[14.5px] leading-[1.6]">
                <div>
                  <Mono>from</Mono> <span className="text-[12px] text-warning">required</span> —
                  verified sender, optionally with display name.
                </div>
                <div>
                  <Mono>to, cc, bcc</Mono> — up to 50 recipients per message.
                </div>
                <div>
                  <Mono>subject</Mono> <span className="text-[12px] text-warning">required</span>
                </div>
                <div>
                  <Mono>html, text, react, template_id</Mono> — pick one; we generate the missing
                  plain-text part.
                </div>
                <div>
                  <Mono>reply_to, headers, attachments, tags</Mono>
                </div>
                <div>
                  <Mono>schedule_at</Mono> — ISO 8601 or natural language (<Mono>"in 1 hour"</Mono>
                  ).
                </div>
              </div>
            </Card>
          </AnchorSection>

          {/*
            The section that is deliberately not ours.

            Every recipe here is another library — the framework's own mailer,
            the `resend` client, `@react-email/components`, a generated OpenAPI
            client — pointed at the reader's deployment. The alternative was a
            first-party integration per framework, which would mean maintaining
            nine half-SDKs to avoid linking to nine whole ones.

            The samples live in content/frameworks.ts as strings rather than
            JSX, because CodeTabs needs the raw text for its copy button and a
            highlighted duplicate would be a second copy to keep in sync.
          */}
          <AnchorSection anchor="frameworks" heading="Sending from your framework">
            <Lede>
              Whatever you are already writing, the integration is a library you already have. The
              base URL is your deployment and the password is your API key; everything else below
              belongs to somebody else’s documentation.
            </Lede>
            <div className="flex flex-col gap-8">
              {FRAMEWORK_GROUPS.map((group) => (
                <div key={group.id}>
                  <h3 className="m-0 mb-1.5 font-display text-[19px] font-medium -tracking-[0.015em]">
                    {group.title}
                  </h3>
                  <p className="mt-0 mb-3.5 max-w-[70ch] text-[15px] leading-[1.7] text-muted">
                    {group.blurb}
                  </p>
                  <CodeTabs items={group.items} caption={group.caption} />
                  {group.note ? (
                    <p className="mt-3 mb-0 text-[14px] leading-[1.7] text-muted-2">{group.note}</p>
                  ) : null}
                </div>
              ))}
            </div>
            <p className="mt-1 mb-0 text-[15px] leading-[1.7] text-muted">
              Longer walkthroughs: <a href="/guides/send-your-first-email">Send your first email</a>
              , <a href="/guides/migrate-from-resend">Migrate from Resend</a> and{' '}
              <a href="/guides/choose-a-sending-transport">Choose a sending transport</a>.
            </p>
          </AnchorSection>

          <AnchorSection anchor="batch" heading="Batch & scheduling">
            <Lede>
              Batch sends fan out through Cloudflare Queues, so one slow recipient domain never
              blocks the rest. Scheduled mail is held in a Durable Object alarm — cancel or
              reschedule any time before it fires.
            </Lede>
            <Code>
              {'await ms.emails.batch([\n  { from: f, to: '}
              <Str>'a@example.com'</Str>
              {', subject: '}
              <Str>'Receipt'</Str>
              {', template_id: '}
              <Str>'tpl_r1'</Str>
              {' },\n  { from: f, to: '}
              <Str>'b@example.com'</Str>
              {', subject: '}
              <Str>'Receipt'</Str>
              {', template_id: '}
              <Str>'tpl_r1'</Str>
              {' },\n], { schedule_at: '}
              <Str>'2026-09-10T09:00:00Z'</Str>
              {' });'}
            </Code>
          </AnchorSection>

          <AnchorSection anchor="attachments" heading="Attachments">
            <Lede>
              Pass base64 content, or a URL we fetch at send time. Files land in R2 in your own
              bucket when self-hosting.
            </Lede>
            {/*
              The artboard claimed a flat "25 MB total per message, matching
              Cloudflare's message ceiling". Cloudflare Email Service caps a
              send at 5 MiB, and only allows 25 MiB to destinations it has
              verified — so a single number here would bounce mail that the
              docs promised would send.
            */}
            <Callout variant="warn" title="Size limits are per transport" className="mb-4">
              <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                <li>
                  <Mono>cloudflare</Mono> — 5 MiB per message, raised to 25 MiB only when the
                  destination address is verified.
                </li>
                <li>
                  <Mono>ses</Mono> — 40 MB per message, including encoding overhead.
                </li>
                <li>
                  <Mono>smtp</Mono> — whatever the carrying transport allows; the relay adds no
                  ceiling of its own.
                </li>
              </ul>
              The API rejects an oversized attachment before the send is queued, so you get a 422
              rather than a bounce.
            </Callout>
            <Code>
              {'attachments: [\n  { filename: '}
              <Str>'invoice.pdf'</Str>
              {', path: '}
              <Str>'https://cdn.acme.dev/inv/4821.pdf'</Str>
              {' },\n  { filename: '}
              <Str>'terms.txt'</Str>
              {', content: base64, content_type: '}
              <Str>'text/plain'</Str>
              {' },\n]'}
            </Code>
          </AnchorSection>

          <AnchorSection anchor="idempotency" heading="Idempotency & tags">
            <Lede>
              Send <Mono>Idempotency-Key</Mono> and a retry inside 24 hours returns the original
              email instead of a duplicate. Tags are indexed, so every chart and log filter can
              slice by them.
            </Lede>
            <Code>
              {'-H '}
              <Str>"Idempotency-Key: order-4821-receipt"</Str>
              {'\n\ntags: [\n  { name: '}
              <Str>'category'</Str>
              {', value: '}
              <Str>'receipt'</Str>
              {' },\n  { name: '}
              <Str>'plan'</Str>
              {',     value: '}
              <Str>'pro'</Str>
              {' },\n]'}
            </Code>
          </AnchorSection>

          <AnchorSection anchor="templates" heading="Templates (react-email, MJML, Handlebars)">
            {/*
              Two paths, and the order matters because only one of them is
              react-email itself.

              `react:` is the real library: sdk-node/src/render.ts detects the
              element structurally, dynamically imports the caller's own
              `@react-email/render`, and posts HTML — the same mechanism the
              `resend` SDK uses, which is why a component written for Resend
              renders here unchanged. `templates push` is a *subset* with the
              same shape, and saying so in that order is the difference between
              a claim that survives contact with a reader's codebase and one
              that does not.
            */}
            <Lede>
              Write{' '}
              <a href="https://react.email" rel="noreferrer">
                react-email
              </a>{' '}
              components — the same <Mono>@react-email/components</Mono> you already have — and pass
              one as <Mono>react</Mono>. The SDK renders it where your code runs and posts the HTML;
              React never reaches the wire, and there is nothing to push or store.
            </Lede>
            <Code>
              <Com>{'// emails/LoginCode.tsx'}</Com>
              {'\nimport { Html, Text, Button } from '}
              <Str>'@react-email/components'</Str>
              {
                ';\n\nexport const LoginCode = ({ code }) => (\n  <Html>\n    <Text>Your code is {code}</Text>\n    <Button href='
              }
              <Str>"https://acme.dev/verify"</Str>
              {'>Verify</Button>\n  </Html>\n);\n\n'}
              <Com>{'// anywhere — npm i mailysend @react-email/components'}</Com>
              {'\nawait ms.emails.send({ from, to, subject: '}
              <Str>'Your code'</Str>
              {', react: LoginCode({ code }) });'}
            </Code>
            <p className="mt-3.5 mb-0 text-[15px] leading-[1.7] text-muted">
              <Mono>@react-email/render</Mono> is an optional peer dependency, imported only if you
              pass <Mono>react</Mono> — so the SDK still installs with no dependencies for everyone
              else. The official <Mono>resend</Mono> client does the same thing, which is why a
              component written for Resend renders here unchanged.
            </p>
            <h3 className="mt-7 mb-1.5 font-display text-[19px] font-medium -tracking-[0.015em]">
              Or store them server-side
            </h3>
            <Lede>
              Push a template instead and it is versioned, previewable and sent by{' '}
              <Mono>template_id</Mono>, so marketing can fix a typo without a deploy and roll back
              instantly. <Mono>.mjml</Mono> and <Mono>.hbs</Mono> files push the same way.
            </Lede>
            <Code>
              <Com>$ npx mailysend templates push # versioned, instant rollback</Com>
            </Code>
            <p className="mt-3.5 mb-0 text-[15px] leading-[1.7] text-muted">
              Worth knowing what that path is and is not: <Mono>templates push</Mono> does not run
              react-email, it compiles <em>react-email-shaped</em> JSX into a data-only AST —
              fifteen component names, plain HTML tags, <Mono>{'{expr}'}</Mono>,{' '}
              <Mono>{'{cond && …}'}</Mono>, <Mono>{'{items.map(…)}'}</Mono> and the <Mono>f.*</Mono>{' '}
              filters. Anything outside that is refused at push time with a diagnostic pointing at
              the line. It is a subset, deliberately: nothing is ever evaluated when the mail
              renders, which is why a stored template cannot become code execution.
            </p>
          </AnchorSection>

          <AnchorSection anchor="audiences" heading="Audiences & contacts">
            <Lede>
              Contacts live in D1 with arbitrary custom fields. Segments are saved SQL-ish filters
              that stay live — <Mono>plan = 'pro' AND last_open &lt; 30d</Mono>.
            </Lede>
            <div className="flex flex-col gap-2">
              <Endpoint method="POST" path="/v1/audiences" />
              <Endpoint method="POST" path="/v1/audiences/:id/contacts" />
              <Endpoint method="GET" path="/v1/audiences/:id/segments/:sid/count" />
              <Endpoint
                method="DEL"
                path="/v1/contacts/:id"
                note="· GDPR erase, cascades everywhere"
              />
            </div>
          </AnchorSection>

          <AnchorSection anchor="broadcasts" heading="Broadcasts">
            <Lede>
              Create in the API or the visual editor — either one is editable in both places. A
              broadcast’s progress lives in a Durable Object, so you get live counts, pause/resume,
              throttle control and per-link click maps.
            </Lede>
            <Code>
              {'const b = await ms.broadcasts.create({\n  audience_id: '}
              <Str>'aud_2Kx'</Str>
              {',\n  from: '}
              <Str>'news@yourdomain.com'</Str>
              {',\n  subject: '}
              <Str>'September changelog'</Str>
              {',\n  html: '}
              <Str>{"'<p>Hi {{first_name}} …</p>'"}</Str>
              {',\n  ab_test: { subject_b: '}
              <Str>'What shipped in September'</Str>
              {
                ', split: 0.2 },\n});\nawait ms.broadcasts.send(b.id, { throttle_per_minute: 5000 });'
              }
            </Code>
            <p className="mt-3.5 mb-0 text-[15px] leading-[1.7] text-muted">
              Throttles, warm-up and what a million-recipient send actually does:{' '}
              <a href="/guides/broadcasts-at-scale">Broadcasts at scale →</a>
            </p>
          </AnchorSection>

          {/*
            The artboard badged this section `RESEND DOESN'T`. Resend ships
            automations now, so the badge was simply false — and the claim was
            never the point. What is durable is where the workflow runs.
          */}
          <AnchorSection anchor="automations" heading="Automations" badge={OWNERSHIP_BADGE}>
            <Lede>
              Drip sequences, welcome flows and win-backs run on Cloudflare Workflows: durable
              steps, waits measured in days, branching on your own events. No external
              orchestration, no cron soup — and the workflow executes in your account, against your
              data.
            </Lede>
            <Code>
              {'await ms.automations.create({\n  name: '}
              <Str>'Onboarding'</Str>
              {',\n  trigger: { event: '}
              <Str>'user.signed_up'</Str>
              {' },\n  steps: [\n    { send: '}
              <Str>'tpl_welcome'</Str>
              {' },\n    { wait: '}
              <Str>'2 days'</Str>
              {' },\n    { branch: { if: '}
              <Str>'contact.projects == 0'</Str>
              {',\n                then: [{ send: '}
              <Str>'tpl_nudge'</Str>
              {' }] } },\n  ],\n});'}
            </Code>
            <p className="mt-3.5 mb-0 text-[15px] leading-[1.7] text-muted">
              One workflow instance per contact, or one per cohort — the choice and its cost:{' '}
              <a href="/guides/automations-instance-vs-cohort">Instance vs cohort →</a>
            </p>
          </AnchorSection>

          <AnchorSection anchor="inbound" heading="Inbound email">
            <Lede>
              Point a catch-all rule at MailySend and inbound mail arrives as parsed JSON: headers,
              text, HTML, attachments in R2, spam score, and the thread it belongs to.
              Sub-addressing (<Mono>ticket+8f2@yourdomain.com</Mono>) and HMAC-signed reply headers
              keep replies routed to the right object.
            </Lede>
            <Code>
              {'{\n  '}
              <Key>"type"</Key>
              {': '}
              <Str>"email.received"</Str>
              {',\n  '}
              <Key>"thread_id"</Key>
              {': '}
              <Str>"thr_5Nq"</Str>
              {',\n  '}
              <Key>"from"</Key>
              {': '}
              <Str>"ana@acme.dev"</Str>
              {',\n  '}
              <Key>"spam_score"</Key>
              {': 0.02,\n  '}
              <Key>"attachments"</Key>
              {': [{ '}
              <Key>"r2_key"</Key>
              {': '}
              <Str>"inb/5Nq/photo.png"</Str>
              {' }]\n}'}
            </Code>
            <p className="mt-3.5 mb-0 text-[15px] leading-[1.7] text-muted">
              MX records, routing rules, mailboxes and threading, end to end:{' '}
              <a href="/guides/receive-email">Receive email →</a>
            </p>
          </AnchorSection>

          <AnchorSection anchor="webhooks" heading="Webhooks">
            <Lede>
              Signed with an HMAC timestamp, retried with exponential backoff for 24 hours, and
              replayable from the dashboard. Every attempt keeps its response code and body so you
              can debug your own endpoint.
            </Lede>
            <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
              {WEBHOOK_EVENTS.map((event) => (
                <li key={event}>
                  <MonoChip size="md" className="text-[12px]">
                    {event}
                  </MonoChip>
                </li>
              ))}
            </ul>
          </AnchorSection>

          <AnchorSection anchor="suppressions" heading="Suppressions">
            <Lede>
              Hard bounces and complaints are suppressed automatically in KV, per workspace, within
              milliseconds — and a suppressed send returns a clear{' '}
              <Mono>422 suppressed_recipient</Mono> instead of silently vanishing. Import your
              existing list on day one so you never re-mail a dead address.
            </Lede>
          </AnchorSection>

          <AnchorSection anchor="analytics" heading="Analytics API">
            <Lede>
              Query the same Analytics Engine data the dashboard charts, grouped by tag, template,
              domain, recipient provider or country — and export raw events to R2 or your warehouse.
            </Lede>
            <Code>
              {
                'GET /v1/analytics/deliverability\n  ?group_by=recipient_provider&tag=receipt&range=30d\n\n{ '
              }
              <Key>"gmail.com"</Key>
              {':   { delivered: 0.997, inbox_rate: 0.981, opens: 0.62 },\n  '}
              <Key>"outlook.com"</Key>
              {': { delivered: 0.991, inbox_rate: 0.943, opens: 0.48 } }'}
            </Code>
            {/*
              `delivered` is an SMTP 250 and `inbox_rate` is not: no placement
              figure can be derived from delivery events, so the API labels its
              provenance the same way the Analytics page does.
            */}
            <p className="mt-3.5 mb-0 text-[15px] text-muted">
              Every <Mono>inbox_rate</Mono> carries a <Mono>source</Mono> field — <Mono>seed</Mono>,{' '}
              <Mono>postmaster</Mono>, <Mono>snds</Mono> or <Mono>estimate</Mono> — because an SMTP{' '}
              <Mono>250</Mono> means accepted, not inboxed.{' '}
              <a href="/analytics">See what the analytics look like →</a>
            </p>
          </AnchorSection>

          <AnchorSection anchor="providers" heading="Providers: Cloudflare, Amazon SES, Resend">
            <Lede>
              MailySend separates the API you code against from the wire that carries the mail.
              Cloudflare Email Service is the default. Point a domain at Amazon SES for the cheapest
              bulk rate, or keep sending through Resend while you migrate — same SDK, same logs,
              same webhooks.
            </Lede>
            <div className="mb-4 overflow-x-auto rounded-tile border border-line bg-card">
              <table className="w-full min-w-[560px] border-collapse text-[14px]">
                <caption className="sr-only">Sending providers and their rates</caption>
                <thead>
                  <tr className="border-b border-line text-left font-mono text-[11px] text-muted-2">
                    <th className="px-3.5 py-3 font-normal">PROVIDER</th>
                    <th className="px-3.5 py-3 font-normal">RATE</th>
                    <th className="px-3.5 py-3 font-normal">BEST FOR</th>
                    <th className="px-3.5 py-3 font-normal">SETUP</th>
                  </tr>
                </thead>
                <tbody>
                  {PROVIDER_ROWS.map(([provider, rate, bestFor, setup]) => (
                    <tr key={provider} className="border-b border-line-soft last:border-0">
                      <th scope="row" className="px-3.5 py-3.5 text-left font-semibold">
                        {provider}
                      </th>
                      <td className="px-3.5 py-3.5 text-muted">{rate}</td>
                      <td className="px-3.5 py-3.5 text-muted">{bestFor}</td>
                      <td className="px-3.5 py-3.5 text-muted">{setup}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Code>
              {'await ms.domains.update('}
              <Str>'dom_9f2'</Str>
              {', {\n  provider: '}
              <Str>'ses'</Str>
              {',            '}
              <Com>{"// 'cloudflare' | 'ses' | 'resend'"}</Com>
              {'\n  credentials_secret: '}
              <Str>'SES_KEY'</Str>
              {',\n  failover: ['}
              <Str>'cloudflare'</Str>
              {'],  '}
              <Com>{'// auto-retry elsewhere on 5xx'}</Com>
              {'\n});'}
            </Code>
            {/*
              The trade-offs between the three used to be re-argued here in
              full. They are a whole guide, and a summary that drifts from it is
              worse than a link to it.
            */}
            <p className="mt-3.5 mb-0 text-[15px] leading-[1.7] text-muted">
              Which one, and when to move:{' '}
              <a href="/guides/choose-a-sending-transport">Choose a sending transport</a>. Migrating
              off Resend? Keep <Mono>provider: 'resend'</Mono> on day one, move traffic percentage
              by percentage, then flip to Cloudflare when the charts look boring —{' '}
              <a href="/guides/migrate-from-resend">Migrate from Resend →</a>
            </p>
          </AnchorSection>

          <AnchorSection anchor="smtp" heading="SMTP relay">
            <div className="rounded-tile border border-line bg-card p-[18px] font-mono text-[13px] leading-[2] text-ink">
              <div>
                {/* The relay is a container you run — see the note below —
                    so this host is yours, not one we operate. */}
                host<span className="text-muted-2"> ····· </span>smtp.yourdomain.com
              </div>
              <div>
                port<span className="text-muted-2"> ····· </span>587 (STARTTLS) · 465 (TLS) · 2587
              </div>
              <div>
                user<span className="text-muted-2"> ····· </span>mailysend
              </div>
              <div>
                pass<span className="text-muted-2"> ····· </span>your API key
              </div>
            </div>
            <p className="mt-3.5 mb-4 text-[15px] leading-[1.7] text-muted">
              Rails, Django, Laravel, WordPress, Jira, anything legacy — their own mailer
              configuration is the whole integration, and it is written out per framework in{' '}
              <a href="#frameworks">Sending from your framework</a>. SMTP sends appear in the same
              logs, analytics and webhooks as API sends.
            </p>
            {/*
              Workers has no inbound TCP listener, so the relay cannot be part
              of the Worker deploy the rest of these docs describe. Saying so
              here is cheaper than a self-hoster discovering it at :587.
            */}
            <Callout variant="info" title="Self-hosting the relay">
              Workers cannot accept inbound TCP, so <Mono>smtp.&lt;domain&gt;:587</Mono> is not part
              of the Worker deploy — it ships as an OCI container image you run wherever you run
              containers, pointed at your MailySend API key. If you would rather not operate one,
              configure your app against Cloudflare’s own relay at{' '}
              <Mono>smtp.mx.cloudflare.net:465</Mono> instead. The trade-off is worth stating
              plainly: those sends bypass MailySend entirely, so they will <strong>not</strong>{' '}
              appear in your logs, analytics or webhooks.
            </Callout>
          </AnchorSection>

          <AnchorSection anchor="sdks" heading="SDKs & OpenAPI">
            <Lede>
              One first-party SDK — Node and TypeScript, MIT, with a Resend-compatible shim — and
              the OpenAPI document every other language generates from, served by your own
              deployment at <Mono>/v1/openapi.json</Mono> so a generated client can never drift from
              the API it was generated against. The per-stack recipes are in{' '}
              <a href="#frameworks">Sending from your framework</a>.
            </Lede>
            <ul className="m-0 grid list-none grid-cols-[repeat(auto-fit,minmax(210px,1fr))] gap-2.5 p-0">
              {SDKS.map(([name, install, state]) => (
                <li key={name} className="rounded-code border border-line bg-card p-3.5">
                  <div className="flex items-center gap-2 text-[14.5px] font-semibold">
                    {name}
                    {state === 'published' ? (
                      <span className="rounded-chip bg-positive-bg px-1.5 py-0.5 font-mono text-[10px] tracking-[0.08em] text-positive">
                        SHIPPED
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-1.5 font-mono text-[12px] text-muted">{install}</div>
                </li>
              ))}
            </ul>
          </AnchorSection>

          {/*
            The CLI gets its own section, and its nav label says "optional",
            because the quickstart above no longer contains a single command.
            It used to end with `npx mailysend tail`, which quietly made a
            terminal a prerequisite for reading your own delivery log.
          */}
          <AnchorSection anchor="cli" heading="The CLI, if you prefer a terminal">
            <Lede>
              Nothing above this line needs it. <Mono>npm i -g mailysend</Mono>, or run it with{' '}
              <Mono>npx</Mono> — the same package as the SDK, so installing one gets you both. It
              exists for people who would rather stay in a shell, for CI, and for getting back into
              a deployment you cannot sign in to.
            </Lede>
            {/*
              These are checked against `cli/src/main.ts` and the flag specs
              beside it, because the block is `copyable` — every line here is
              something a reader will paste into a shell, so a flag that does
              not exist is a failed command rather than a typo in prose.

              That check is now `cli/test/documented-commands.test.ts`, which
              reads every `mailysend …` on this site and resolves it against
              ENTRIES and the FlagSpecs. It was written because this comment was
              the only place the claim existed, and six invocations elsewhere on
              the site had drifted out from under it.

              `tail` used to read `--tag receipt --status bounced`; it takes
              neither, and the thing it does take is `--filter`, a list of event
              types. `deploy --domain acme.dev` never existed either — see the
              note on DEPLOY_LINES in components/marketing/deploy.tsx, which is
              where the correct deploy pair lives.

              `deploy` is deliberately not in this list. It is the button's job
              on this page, and a reader who wants the wrangler path is better
              served by the deploy guide, which explains the config flag.
            */}
            <Terminal
              className="mt-3.5"
              copyable
              lines={[
                { kind: 'command', text: 'npx mailysend login --url https://mail.acme.dev' },
                {
                  kind: 'command',
                  text: 'npx mailysend send --from hi@acme.dev --to me@acme.dev --template tpl_welcome',
                },
                { kind: 'command', text: 'npx mailysend tail --status bounced' },
                { kind: 'command', text: 'npx mailysend domains verify acme.dev' },
              ]}
            />
            <p className="mt-3.5 mb-0 text-[15px] leading-[1.7] text-muted">
              Two jobs are worth knowing it exists for even if you never use the rest.{' '}
              <Mono>npx mailysend claim</Mono> takes ownership of a deployment, or recovers one you
              are locked out of — the break-glass path when passkeys and recovery codes are both
              gone. And <Mono>npx mailysend provision</Mono> creates the queues for a deploy driven
              by wrangler or CI rather than by the button, which does it during the build.{' '}
              <a href="/guides/deploy-to-cloudflare#run-it">The wrangler path →</a>
            </p>
          </AnchorSection>

          <AnchorSection anchor="mcp" heading="MCP & agents">
            <Lede>
              Your workspace exposes an MCP endpoint at <Mono>/mcp</Mono> with nine tools: search
              threads, read a message, reply, send, look up delivery status, list domains, read
              analytics, manage contacts. The two that send mail never send on their first call —
              they return a confirmation bound to that exact message, which a person approves at{' '}
              <Mono>/app/approvals</Mono>. There is no MCP method that approves one, so an agent
              holding a valid key still cannot approve its own send.
            </Lede>
            <Code>
              {'{\n  '}
              <Key>"mcpServers"</Key>
              {': {\n    '}
              <Key>"mailysend"</Key>
              {': {\n      '}
              <Key>"url"</Key>
              {': '}
              <Str>"https://your-deployment/mcp"</Str>
              {',\n      '}
              <Key>"headers"</Key>
              {': { '}
              <Key>"Authorization"</Key>
              {': '}
              <Str>"Bearer ms_live_…"</Str>
              {' }\n    }\n  }\n}'}
            </Code>
          </AnchorSection>

          <AnchorSection anchor="errors" heading="Errors & rate limits">
            <Lede>
              Errors are typed, human-readable, and always name the fix. Rate limits are a fixed
              window per workspace, not per key: 600 sends a minute on <Mono>/v1/emails</Mono> and
              1,000 requests a minute everywhere else. Inbound is never limited.
            </Lede>
            <div className="overflow-x-auto rounded-tile border border-line bg-card">
              <table className="w-full min-w-[520px] border-collapse text-[14px]">
                <caption className="sr-only">Error codes and their remedies</caption>
                <thead>
                  <tr className="border-b border-line text-left font-mono text-[11px] text-muted-2">
                    <th className="w-[70px] px-3.5 py-3 font-normal">CODE</th>
                    <th className="px-3.5 py-3 font-normal">TYPE</th>
                    <th className="px-3.5 py-3 font-normal">WHAT TO DO</th>
                  </tr>
                </thead>
                <tbody>
                  {ERROR_ROWS.map(([code, type, remedy]) => (
                    <tr key={code} className="border-b border-line-soft last:border-0">
                      <th scope="row" className="px-3.5 py-3 text-left font-mono text-[13px]">
                        {code}
                      </th>
                      <td className="px-3.5 py-3 font-mono text-[13px]">{type}</td>
                      <td className="px-3.5 py-3 text-muted">{remedy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AnchorSection>

          <section className="flex flex-wrap items-center justify-between gap-4 rounded-panel border border-line bg-card p-[26px]">
            <div className="min-w-0">
              <h2 className="m-0 font-display text-[22px] font-medium -tracking-[0.02em]">
                Ready to send?
              </h2>
              <p className="mt-1 mb-0 text-[15px] text-muted">
                Deploy the platform into your own Cloudflare account in {DEPLOY_DURATION_LONG}. MIT
                licensed, no vendor bill.
              </p>
            </div>
            <div className="flex flex-wrap gap-2.5">
              <Button asChild>
                <a href={DEPLOY_URL} rel="noreferrer">
                  Deploy to Cloudflare
                </a>
              </Button>
              <Button asChild variant="outline">
                <a href="/guides/deploy-to-cloudflare">Deploy guide</a>
              </Button>
            </div>
          </section>
        </article>
      </div>
    </PageShell>
  )
}
